import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TCustomerReduxState } from '~types/redux/state';
import {
    TCreateCollateralReduxParams,
    TCreateNewCustomerReduxParams,
    TCreateReleaseEarmarkReduxParams,
    TCustomerDetails,
    TCustomerListing,
    TDeleteCustomerCollateralReduxParams,
    TEditCustomerDetailsReduxParams,
    TExportCustomerListingAPIResponse,
    TGetCollateralListingReduxResponse,
    TGetCollateralMarketValueParams,
    TGetCustomerCollateralReduxParams,
    TGetCustomerDetailsReduxParams,
    TGetCustomerFilterAPIResponse,
    TGetCustomerFilterReduxParams,
    TGetCustomerInventoryAPIResponse,
    TGetCustomerInventoryReduxParams,
    TGetCustomerListingAPIResponse,
    TGetCustomerListingFilters,
    TGetCustomerTransactionsAPIResponse,
    TGetCustomerTransactionsReduxParams,
} from '~types/feat/customer';
import {
    CustomerAccFundTypeEnum,
    CustomerAccStatusEnum,
    CustomerIdTypeEnum,
    CustomerRaceEnum,
    MetalTypeEnum,
    TransactionStatusEnum,
} from '~utils/enums';

export const initialState: TCustomerReduxState = {
    loadings: {
        getCustomerListing: false,
        createNewCustomer: false,
        editCustomerDetails: false,
        deleteCustomer: false,
        getCustomerDetails: false,
        getCustomerTransactions: false,
        getCustomerCollateral: false,
        getAllCustomers: false,
        getCustomerInventory: false,
        exportCustomerListing: false,
        createCollateral: false,
        getCollateralMarketValue: false,
        cancelCustomerCollateral: false,
        updateReleaseEarmark: false,
    },
    customerListing: {
        index: 0,
        maxIndex: 0,
        filters: {
            name: '',
            idNo: '',
            branchName: '',
            branchCode: '',
            status: '',
            dateFrom: '',
            dateTo: '',
        },
        data: [],
    },
    modals: {
        createCollateralModalIsOpen: false,
        createNewCustomerModalIsOpen: false,
        customerDetailsModalIsOpen: false,
        customerPaperGoldBalanceIsOpen: false,
        deleteCustomerModalIsOpen: false,
        deleteCustomerCollateralModalIsOpen: false,
        isEditing: false,
        collateralDetailsModalIsOpen: false,
        isCreateCollateralFormFilled: false,
        confirmReleaseEarmarkModalIsOpen: false,
    },
    customerDetails: {
        customerId: 0,
        name: '',
        email: '',
        CIFNo: '',
        idType: CustomerIdTypeEnum.IC,
        idNo: '',
        contactNo: '',
        regionName: '',
        branchCode: '',
        branchName: '',
        branchId: 0,
        // gender: CustomerGenderEnum.Male,
        nationality: '',
        occupation: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postCode: '',
        race: CustomerRaceEnum.Malay,
        // dateOfBirth: '',
        fundType: CustomerAccFundTypeEnum.Islamic,
        paperGoldBalance: 0,
        goldEarmarkBalance: 0,
        goldEarmarkEndDate: '',
        silverEarmarkBalance: 0,
        silverEarmarkEndDate: '',
        paperSilverBalance: 0,
        refCode: '',
        status: CustomerAccStatusEnum.Active,
        createdAt: '',
        updatedAt: '',
    },
    customerTransactions: {
        index: 0,
        maxIndex: 0,
        data: [],
    },
    customerCollateral: {
        index: 0,
        maxIndex: 0,
        data: [],
    },
    allCustomers: [],
    customerInventory: [],
    customerListingForExport: [],
    createCollateralParams: {
        customerId: 0,
        productId: 0,
        productName: undefined,
        amount: 0,
        goldPrice: undefined,
        remarks: undefined,
        collateralEndDate: '',
        marketValue: undefined,
        metalType: MetalTypeEnum.Gold,
    },
    tempCollateralState: {
        customerId: 0,
        collateralId: 0,
    },
    tempReleaseEarmarkState: {
        transactionId: 0,
    },
    selectedCollateralMetalType: MetalTypeEnum.Gold,
    errors: {
        getCustomerListing: '',
        createNewCustomer: '',
        editCustomerDetails: '',
        deleteCustomer: '',
        getCustomerDetails: '',
        getCustomerTransactions: '',
        getCustomerCollateral: '',
        getAllCustomers: '',
        getCustomerInventory: '',
        exportCustomerListing: '',
        createCollateral: '',
        getCollateralMarketValue: '',
        cancelCustomerCollateral: '',
        updateReleaseEarmark: '',
    },
};

const customerSlice = createSlice({
    name: 'customer',
    initialState,
    reducers: {
        customerGetCustomerListingAttempt: (state) => {
            state.loadings.getCustomerListing = true;
            state.errors.getCustomerListing = '';
        },
        customerGetCustomerListingSuccess: (state, action: PayloadAction<TGetCustomerListingAPIResponse>) => {
            state.loadings.getCustomerListing = false;
            state.customerListing = {
                filters: state.customerListing.filters,
                ...action.payload,
            };
        },
        customerGetCustomerListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCustomerListing = false;
            if (action.payload) state.errors.getCustomerListing = action.payload;
        },
        customerSetGetCustomerListingFilters: (state, action: PayloadAction<TGetCustomerListingFilters>) => {
            state.customerListing.filters = action.payload;
        },
        customerSetCustomerListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.customerListing.index = action.payload.index;
        },
        customerSetCustomerListingData: (state, action: PayloadAction<TCustomerListing[]>) => {
            state.customerListing.data = action.payload;
        },
        customerSetCreateNewCustomerModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.createNewCustomerModalIsOpen = action.payload;
        },
        customerCreateNewCustomerAttempt: (state, _action: PayloadAction<TCreateNewCustomerReduxParams>) => {
            state.loadings.createNewCustomer = true;
            state.errors.createNewCustomer = '';
        },
        customerCreateNewCustomerSuccess: (state) => {
            state.loadings.createNewCustomer = false;
        },
        customerCreateNewCustomerFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createNewCustomer = false;
            if (action.payload) state.errors.createNewCustomer = action.payload;
        },
        customerResetCreateNewCustomerState: (state) => {
            state.loadings.createNewCustomer = initialState.loadings.createNewCustomer;
            state.errors.createNewCustomer = initialState.errors.createNewCustomer;
        },
        customerSetCustomerDetailsModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.customerDetailsModalIsOpen = action.payload;
        },
        customerSetCustomerDetailsModalIsEditing: (state, action: PayloadAction<boolean>) => {
            state.modals.isEditing = action.payload;
        },
        customerResetCustomerDetails: (state) => {
            state.customerDetails = initialState.customerDetails;
        },
        customerGetCustomerDetailsAttempt: (state, _action: PayloadAction<TGetCustomerDetailsReduxParams>) => {
            state.loadings.getCustomerDetails = true;
            state.errors.getCustomerDetails = '';
        },
        customerGetCustomerDetailsSuccess: (state, action: PayloadAction<TCustomerDetails>) => {
            state.loadings.getCustomerDetails = false;
            state.customerDetails = action.payload;
        },
        customerGetCustomerDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCustomerDetails = false;
            if (action.payload) state.errors.getCustomerDetails = action.payload;
        },
        customerEditCustomerDetailsAttempt: (state, _action: PayloadAction<TEditCustomerDetailsReduxParams>) => {
            state.loadings.editCustomerDetails = true;
            state.errors.editCustomerDetails = '';
        },
        customerEditCustomerDetailsSuccess: (state) => {
            state.loadings.editCustomerDetails = false;
        },
        customerEditCustomerDetailsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.editCustomerDetails = false;
            if (action.payload) state.errors.editCustomerDetails = action.payload;
        },
        customerResetEditCustomerDetailsState: (state) => {
            state.loadings.editCustomerDetails = initialState.loadings.editCustomerDetails;
            state.errors.editCustomerDetails = initialState.errors.editCustomerDetails;
        },
        customerSetDeleteCustomerModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.deleteCustomerModalIsOpen = action.payload;
        },
        customerDeleteCustomerAttempt: (state, _action: PayloadAction<TEditCustomerDetailsReduxParams>) => {
            state.loadings.deleteCustomer = true;
            state.errors.deleteCustomer = '';
        },
        customerDeleteCustomerSuccess: (state) => {
            state.loadings.deleteCustomer = false;
        },
        customerDeleteCustomerFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.deleteCustomer = false;
            if (action.payload) state.errors.deleteCustomer = action.payload;
        },
        customerResetCustomerState: (state) => {
            state.loadings.deleteCustomer = initialState.loadings.deleteCustomer;
            state.errors.deleteCustomer = initialState.errors.deleteCustomer;
        },
        customerSetCustomerTransactionsIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.customerTransactions.index = action.payload.index;
        },
        customerGetCustomerTransactionsAttempt: (state, _action: PayloadAction<TGetCustomerTransactionsReduxParams>) => {
            state.loadings.getCustomerTransactions = true;
            state.errors.getCustomerTransactions = '';
        },
        customerGetCustomerTransactionsSuccess: (state, action: PayloadAction<TGetCustomerTransactionsAPIResponse>) => {
            state.loadings.getCustomerTransactions = false;
            state.customerTransactions = action.payload;
        },
        customerGetCustomerTransactionsFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCustomerTransactions = false;
            if (action.payload) state.errors.getCustomerTransactions = action.payload;
        },
        customerResetAllCustomersState: (state) => {
            state.allCustomers = initialState.allCustomers;
        },
        customerGetAllCustomersAttempt: (state, _action: PayloadAction<TGetCustomerFilterReduxParams>) => {
            state.loadings.getAllCustomers = true;
            state.errors.getAllCustomers = '';
        },
        customerGetAllCustomersSuccess: (state, action: PayloadAction<TGetCustomerFilterAPIResponse>) => {
            state.loadings.getAllCustomers = false;
            state.allCustomers = action.payload.data;
        },
        customerGetAllCustomersFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getAllCustomers = false;
            if (action.payload) state.errors.getAllCustomers = action.payload;
        },
        customerGetCustomerInventoryAttempt: (state, _action: PayloadAction<TGetCustomerInventoryReduxParams>) => {
            state.loadings.getCustomerInventory = true;
            state.errors.getCustomerInventory = '';
        },
        customerGetCustomerInventorySuccess: (state, action: PayloadAction<TGetCustomerInventoryAPIResponse>) => {
            state.loadings.getCustomerInventory = false;
            state.customerInventory = action.payload;
        },
        customerGetCustomerInventoryFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCustomerInventory = false;
            if (action.payload) state.errors.getCustomerInventory = action.payload;
        },
        customerResetCustomerInventory: (state) => {
            state.customerInventory = initialState.customerInventory;
        },
        customerExportCustomerListingAttempt: (state) => {
            state.loadings.exportCustomerListing = true;
            state.errors.exportCustomerListing = '';
        },
        customerExportCustomerListingSuccess: (state, action: PayloadAction<TExportCustomerListingAPIResponse>) => {
            state.loadings.exportCustomerListing = false;
            state.customerListingForExport = action.payload;
        },
        customerExportCustomerListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportCustomerListing = false;
            if (action.payload) state.errors.exportCustomerListing = '';
        },
        customerClearCustomerListingForExport: (state) => {
            state.customerListingForExport = initialState.customerListingForExport;
        },
        customerResetCreateCollateralState: (state) => {
            state.loadings.createCollateral = initialState.loadings.createCollateral;
            state.errors.createCollateral = initialState.errors.createCollateral;
        },
        customerSetCreateCollateralModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.createCollateralModalIsOpen = action.payload;
        },
        customerSetIsCreateCollateralFormFilled: (state, action: PayloadAction<boolean>) => {
            state.modals.isCreateCollateralFormFilled = action.payload;
        },
        customerSetCreateCollateralParams: (state, action: PayloadAction<TCreateCollateralReduxParams>) => {
            state.createCollateralParams = action.payload;
        },
        customerClearCreateCollateralParams: (state) => {
            state.createCollateralParams = initialState.createCollateralParams;
        },
        customerCreateCollateralAttempt: (state) => {
            state.loadings.createCollateral = true;
            state.errors.createCollateral = '';
        },
        customerCreateCollateralSuccess: (state) => {
            state.loadings.createCollateral = false;
        },
        customerCreateCollateralFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.createCollateral = false;
            if (action.payload) state.errors.createCollateral = action.payload;
        },
        customerGetCollateralMarketValueAttempt: (state, _action: PayloadAction<TGetCollateralMarketValueParams>) => {
            state.loadings.getCollateralMarketValue = true;
            state.errors.getCollateralMarketValue = '';
        },
        customerGetCollateralMarketValueSuccess: (state, action: PayloadAction<number | undefined>) => {
            state.loadings.getCollateralMarketValue = false;
            state.createCollateralParams.marketValue = action.payload;
        },
        customerGetCollateralMarketValueFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCollateralMarketValue = false;
            if (action.payload) state.errors.getCollateralMarketValue = action.payload;
        },
        customerSetCustomerCollateralIndex: (state, action: PayloadAction<{ index: number }>) => {
            state.customerCollateral.index = action.payload.index;
        },
        customerGetCustomerCollateralListingAttempt: (state, _action: PayloadAction<TGetCustomerCollateralReduxParams>) => {
            state.loadings.getCustomerCollateral = true;
            state.errors.getCustomerCollateral = '';
        },
        customerGetCustomerCollateralListingSuccess: (state, action: PayloadAction<TGetCollateralListingReduxResponse>) => {
            state.loadings.getCustomerCollateral = false;
            state.customerCollateral = action.payload;
        },
        customerGetCustomerCollateralListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getCustomerCollateral = false;
            if (action.payload) state.errors.getCustomerCollateral = action.payload;
        },
        customerDeleteCustomerCollateralAttempt: (state, _action: PayloadAction<TDeleteCustomerCollateralReduxParams>) => {
            state.loadings.cancelCustomerCollateral = true;
            state.errors.cancelCustomerCollateral = '';
        },
        customerDeleteCustomerCollateralSuccess: (state) => {
            state.loadings.cancelCustomerCollateral = false;
        },
        customerDeleteCustomerCollateralFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.cancelCustomerCollateral = false;
            if (action.payload) state.errors.getCustomerCollateral = action.payload;
        },
        customerSetCustomerCollateralModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.deleteCustomerCollateralModalIsOpen = action.payload;
        },
        customerSetCustomerCollateralState: (state, action: PayloadAction<TDeleteCustomerCollateralReduxParams>) =>{
            state.tempCollateralState = action.payload;
        },
        customerResetCustomerCollateralState: (state) => {
            state.tempCollateralState = initialState.tempCollateralState;
        },
        customerSetCollateralDetailsModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.collateralDetailsModalIsOpen = action.payload;
        },
        customerSetConfirmUpdateReleaseEarmarkModalIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.confirmReleaseEarmarkModalIsOpen = action.payload;
        },
        customerUpdateReleaseEarmarkAttempt: (state, _action: PayloadAction<TCreateReleaseEarmarkReduxParams>) => {
            state.loadings.updateReleaseEarmark = true;
            state.errors.updateReleaseEarmark = '';
        },
        customerUpdateReleaseEarmarkSuccess: (state) => {
            state.loadings.updateReleaseEarmark = false;
        },
        customerUpdateReleaseEarmarkFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.updateReleaseEarmark = false;
            if (action.payload) state.errors.updateReleaseEarmark = action.payload;
        },
        customerSetReleaseEarmarkState: (state, action: PayloadAction<TCreateReleaseEarmarkReduxParams>) =>{
            state.tempReleaseEarmarkState = action.payload;
        },
        customerResetReleaseEarmarkState: (state) => {
            state.loadings.updateReleaseEarmark = initialState.loadings.updateReleaseEarmark;
            state.errors.updateReleaseEarmark = initialState.errors.updateReleaseEarmark;
            state.tempReleaseEarmarkState = initialState.tempReleaseEarmarkState;
        },
        customerSetCustomerPaperGoldBalanceIsOpen: (state, action: PayloadAction<boolean>) => {
            state.modals.customerPaperGoldBalanceIsOpen = action.payload;
        },
        customerSetSelectedCollateralMetalType: (state, action: PayloadAction<MetalTypeEnum>) => {
            state.selectedCollateralMetalType = action.payload;
        },
    },
});

export default {
    actions: customerSlice.actions,
    reducers: customerSlice.reducer,
};
